import { takeLatest, takeEvery, put, call, delay } from 'redux-saga/effects';
import { groupBy } from 'lodash';
import { googleLogout } from '@react-oauth/google';
import {
  LOAD_SESSION_PERMISSION,
  LOGIN,
  GOOGLE_LOGIN,
  LOGOUT,
} from 'state/actionTypes';
import * as api from 'api/session/session';
import * as toast from 'state/toast/actions';
import {
  clearSessionPermission,
  loginFailure,
  loginSuccess,
  setSessionPermission,
} from 'state/session/actions';

function getLoginErrorMessage(errorCode) {
  switch (errorCode) {
    case 'INVALID_LOGIN':
      return 'Wrong username or password';
    case 'INVALID_TOKEN':
      return 'Google sign-in failed. Please try again.';
    default:
      return errorCode;
  }
}

export function* loginSaga({ payload }) {
  const { username, password } = payload;

  try {
    const {
      data: { data },
    } = yield call(api.performLogin, { username, password });
    yield put(loginSuccess(data));
  } catch (e) {
    if (e.response) {
      yield put(
        toast.errorMessage(
          'Login error',
          getLoginErrorMessage(e.response.data.errors[0])
        )
      );
    } else {
      yield put(toast.errorMessage('Login error', e.message));
    }
    yield put(loginFailure());
  }
}

export function* googleLoginSaga({ payload }) {
  const { idToken } = payload;

  try {
    const {
      data: { data },
    } = yield call(api.performGoogleLogin, idToken);
    yield put(loginSuccess(data));
  } catch (e) {
    if (e.response) {
      yield put(
        toast.errorMessage(
          'Login error',
          getLoginErrorMessage(e.response.data.errors[0])
        )
      );
    } else {
      yield put(toast.errorMessage('Login error', e.message));
    }
    yield put(loginFailure());
  }
}

export function* logoutSaga() {
  yield call(googleLogout);
  yield put(clearSessionPermission());
}

function constructPermissionsMap(permissions) {
  const grouped = groupBy(permissions, p => p.type);
  return Object.entries(grouped).reduce(
    (memo, [type, permission]) =>
      memo.set(type, new Set(permission.map(p => p.name))),
    new Map()
  );
}

export function* loadSessionPermission() {
  yield delay(20); // to prevent calling api.getPermissions twice after login
  const { data: responseData } = yield call(api.getPermissions);
  const permission = {
    roles: responseData.data.roles,
    permissions: constructPermissionsMap(responseData.data.permissions),
    resources: responseData.data.resources,
    pages: new Set(responseData.data.pages.map(p => p.pattern)),
  };

  yield put(setSessionPermission(permission));
}

export default function* saga() {
  yield takeEvery(LOGIN, loginSaga);
  yield takeEvery(GOOGLE_LOGIN, googleLoginSaga);
  yield takeEvery(LOGOUT, logoutSaga);
  yield takeLatest(LOAD_SESSION_PERMISSION, loadSessionPermission);
}
